import { cn } from "@/lib/utils"

import GeneralBackground from "@/assets/img/profile/general_background.webp"
import HeartBroken from "@/assets/img/profile/plan/heart_broken.png"
import HoroscopeIcon from "@/assets/img/today/nav/horoscope.webp"
import ReportsIcon from "@/assets/img/today/nav/reports.webp"
import BiorhythmIcon from "@/assets/img/today/nav/biorhythm.webp"

import { useSheetDismiss } from "../hooks/use-sheet-dismiss"

interface PlanRetentionModalProps {
  isCanceling: boolean
  error: string | null
  onStay: () => void
  onConfirmCancel: () => void
}

interface Highlight {
  icon: string
  title: string
  description: string
}

const HIGHLIGHTS: Highlight[] = [
  {
    icon: HoroscopeIcon,
    title: "Keep your daily predictions",
    description: "Discover love, energy, and timing insights tailored just for you",
  },
  {
    icon: ReportsIcon,
    title: "Don’t lose your deep reports",
    description: "Explore palmistry, compatibility, destiny, and so much more",
  },
  {
    icon: BiorhythmIcon,
    title: "Stay connected to your signs",
    description: "Get guidance from tarot, moon phases, runes, and biorhythms",
  },
]

/** Retention sheet shown before canceling: "We are sorry to see you go". */
export function PlanRetentionModal({
  isCanceling,
  error,
  onStay,
  onConfirmCancel,
}: PlanRetentionModalProps) {
  const { backdropClass, panelClass, dismiss } = useSheetDismiss()

  return (
    <div
      className={cn(
        "fixed inset-0 z-[1100] mx-auto flex min-h-[100dvh] w-full max-w-app flex-col justify-end bg-dark-base/80 backdrop-blur-[10px]",
        backdropClass,
      )}
    >
      <div
        className={cn(
          "relative flex flex-col items-center gap-6 overflow-hidden rounded-t-3xl bg-dark-surface px-4 pb-4 pt-36",
          panelClass,
        )}
      >
        {/* Starry backdrop + broken heart — fades into the surface so the title stays readable */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-60 overflow-hidden">
          <img src={GeneralBackground} alt="" className="absolute inset-0 size-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent from-30% to-dark-surface" />
          <img
            src={HeartBroken}
            alt=""
            className="absolute left-1/2 top-8 w-[108px] -translate-x-1/2 select-none"
          />
        </div>

        <div className="relative z-10 flex w-full flex-col gap-2 text-center">
          <h2 className="font-display text-2xl font-medium text-purple-light">
            We are sorry to see you go
          </h2>
          <p className="font-body text-base text-purple-light/70">
            Your personal guidance, readings, and cosmic tools don’t have to end here
          </p>
        </div>

        <div className="relative z-10 flex w-full flex-col gap-3 rounded-3xl bg-dark-base p-4 shadow-[0_0_16px_rgba(255,83,127,0.2),0_0_32px_#9565FD]">
          {HIGHLIGHTS.map((item, index) => (
            <div key={item.title} className="flex flex-col gap-3">
              {index > 0 && (
                <div className="pl-16">
                  <div className="h-px w-full bg-purple-light/20" />
                </div>
              )}
              <div className="flex items-center gap-3">
                <img
                  src={item.icon}
                  alt=""
                  className="size-[52px] shrink-0 rounded-full border border-purple-light/20 object-cover"
                />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <p className="font-display text-sm font-medium text-purple-light">{item.title}</p>
                  <p className="font-body text-sm text-purple-light/60">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="relative z-10 flex w-full flex-col items-center gap-4">
          {error && <p className="text-center font-body text-sm text-peach">{error}</p>}

          <button
            type="button"
            onClick={() => dismiss(onStay)}
            className="flex h-14 w-full items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
          >
            I’ll stay and keep exploring
          </button>

          <button
            type="button"
            onClick={onConfirmCancel}
            disabled={isCanceling}
            className="font-body text-sm text-purple-light/40 underline underline-offset-2 disabled:opacity-60"
          >
            {isCanceling ? "Canceling…" : "I want to cancel"}
          </button>
        </div>
      </div>
    </div>
  )
}
